<template>
  <!-- A segmented control on the tinted track, the active segment on the
       segment fill — the same pattern as the theme toggle. `block` fills its
       row for the cart, where there is room for both labels. Compact by default
       for the header, where it stands in for the table chip: there the labels
       fall away below sm and each segment is just its icon, the same way the
       account and table controls shed their text on a phone.

       A radio group, not a tablist. It was `role="tablist"` with `role="tab"`
       and no tab panel anywhere, so a screen reader announced navigation for
       what is a choice between two states. -->
  <div
    aria-label="How this order is served"
    class="flex rounded-full bg-surface"
    :class="props.block && 'w-full'"
    role="radiogroup"
  >
    <button
      v-for="option in options"
      :key="option.value"
      :aria-checked="props.modelValue === option.value"
      :aria-label="option.label"
      class="inline-flex min-h-btn-xs items-center justify-center gap-2 rounded-full text-action pj-tap"
      :class="[
        props.block ? 'flex-1 px-4' : 'px-4 max-sm:px-2.5',
        props.modelValue === option.value ? 'bg-segment text-ink shadow-chip' : 'text-muted hover:text-ink'
      ]"
      role="radio"
      type="button"
      @click="emit('update:modelValue', option.value)"
    >
      <PjIcon :name="option.icon" />
      <!-- Kept for the desktop header and the whole cart; dropped on a phone in
           the compact header, where the aria-label carries the name instead.
           A named table always keeps its text: it is the one label on this
           control that a glyph cannot stand in for. -->
      <span :class="!props.block && !option.isTable && 'max-sm:hidden'">{{ option.label }}</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { OrderMode } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'

import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import { ORDER_MODE } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'

const props = defineProps<{
  modelValue: OrderMode
  block?: boolean
  /**
   * The table, once the diner has one. Dining in *is* sitting at a table, so the
   * segment names it rather than leaving a chip beside the control to repeat it.
   */
  dineInLabel?: string | null
}>()

const emit = defineEmits<{ 'update:modelValue': [OrderMode] }>()

const options = computed(() => [
  {
    value: ORDER_MODE.DINE_IN,
    label: props.dineInLabel || 'Dine in',
    icon: props.dineInLabel ? PJ_ICON.LOCATION : PJ_ICON.DINE_IN,
    isTable: !!props.dineInLabel
  },
  { value: ORDER_MODE.TAKEAWAY, label: 'Takeaway', icon: PJ_ICON.TAKEAWAY, isTable: false }
])
</script>
